import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, Compass, ListChecks, Radar, Settings2, ShieldCheck } from "lucide-react";

import { getGrantAffinityProfile, isGrantAffinityProfile } from "@/api/grant_affinity_profile";
import { GrantDiscoverPanel } from "@/components/grant_suite/GrantDiscoverPanel";
import { GrantRadarPanel } from "@/components/grant_suite/GrantRadarPanel";
import { GrantTeamManagerPanel } from "@/components/grant_suite/GrantTeamManagerPanel";
import { GrantTrackingPanel } from "@/components/grant_suite/GrantTrackingPanel";
import { MyMilestonesPanel } from "@/components/grant_suite/MyMilestonesPanel";
import { LaunchPadUnavailableHint } from "@/components/launch_pad/LaunchPadUnavailableHint";
import { useGrantSuiteNavigation } from "@/context/GrantSuiteNavigationContext";
import { MunicipalitySwitcher } from "@/components/launch_pad/MunicipalitySwitcher";
import { useMunicipality } from "@/context/MunicipalityContext";
import { useUser } from "@/context/UserProvider";
import { ModuleWorkspaceBody } from "@/components/module_summary/ModuleWorkspaceBody";
import { ModuleWorkspaceHeader } from "@/components/module_summary/ModuleWorkspaceHeader";
import { moduleWorkspaceShellClass } from "@/components/module_summary/moduleWorkspaceLayout";
import { userHasGrantSuite, userIsGrantTeamManager, userIsOutsideUser } from "@/lib/grantSuiteAccess";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type GrantSuiteTab = "discover" | "radar" | "track" | "my_milestones" | "team_manager";

// Tabs open to Discover-tier users; the rest need the Grant Suite (Track) license.
const DISCOVER_TIER_TABS: GrantSuiteTab[] = ["discover", "radar"];

function resolveInitialGrantTab(
  pendingTab: GrantSuiteTab | null,
  canUseGrantSuite: boolean,
): GrantSuiteTab {
  if (pendingTab === "track" && canUseGrantSuite) return "track";
  return "discover";
}

const TABS: { id: GrantSuiteTab; label: string; icon: typeof Compass }[] = [
  { id: "discover", label: "Discover", icon: Compass },
  { id: "radar", label: "Radar", icon: Radar },
  { id: "track", label: "Track", icon: BookOpen },
  { id: "my_milestones", label: "My milestones", icon: ListChecks },
  { id: "team_manager", label: "Team Manager", icon: ShieldCheck },
];

export function GrantSuiteWorkspace() {
  const { currentUser } = useUser();
  const canUseGrantSuite = userHasGrantSuite(currentUser);
  // Outside collaborators (ENG-1820) get Track only: Discover and milestones
  // are team-member surfaces, and the backend denies them anyway.
  const outsideUser = userIsOutsideUser(currentUser);
  const teamManager = userIsGrantTeamManager(currentUser);
  const { focusRequest, pendingTab, clearGrantOpportunityFocus, clearPendingTab } =
    useGrantSuiteNavigation();
  const { municipality } = useMunicipality();
  const municipalityId = municipality?.id;
  // Grant Radar (ENG-2359) is per-city: the flag lives on the grant workspace team.
  // A city gets Radar instead of Discover, never both.
  const grantRadar = municipality?.grant_radar === true;
  const findTab: GrantSuiteTab = grantRadar ? "radar" : "discover";
  const [tabState, setTab] = useState<GrantSuiteTab>(() =>
    resolveInitialGrantTab(pendingTab, canUseGrantSuite),
  );
  // The one statement of who may see which tab; rendering, selection, and the
  // tab strip all derive from it so they cannot drift apart.
  const tabAvailable = (id: GrantSuiteTab): boolean => {
    if (outsideUser) return id === "track";
    if (id === "team_manager") return teamManager;
    if (id === "radar") return grantRadar;
    if (id === "discover") return !grantRadar;
    return true;
  };
  // Derived, not corrected-after-mount: a role lost while the tab was open
  // (or an outside user with stale state) never renders the tab, even
  // transiently.
  const tab: GrantSuiteTab = tabAvailable(tabState)
    ? tabState
    : outsideUser
    ? "track"
    : findTab;
  const [lockedHintTab, setLockedHintTab] = useState<GrantSuiteTab | null>(null);
  const [profileWizardOpen, setProfileWizardOpen] = useState(false);

  useEffect(() => {
    if (!pendingTab) return;
    if (pendingTab === "track" && !canUseGrantSuite) {
      clearPendingTab();
      return;
    }
    setTab(pendingTab);
    clearPendingTab();
  }, [pendingTab, clearPendingTab, canUseGrantSuite]);

  const { data: profileResponse } = useQuery({
    queryKey: ["grant_affinity_profile", municipalityId],
    queryFn: () => getGrantAffinityProfile(municipalityId!),
    enabled: tab === "discover" && Boolean(municipalityId),
  });
  const affinityProfile =
    profileResponse && isGrantAffinityProfile(profileResponse) ? profileResponse : null;
  const profileComplete = affinityProfile?.complete === true && affinityProfile?.embedded === true;

  const selectTab = (next: GrantSuiteTab) => {
    if (!tabAvailable(next)) return;
    if (!DISCOVER_TIER_TABS.includes(next) && !canUseGrantSuite) return;
    setTab(next);
  };

  const visibleTabs = TABS.filter((item) => tabAvailable(item.id));

  return (
    <div className={cn(moduleWorkspaceShellClass, "flex-1 min-h-0")} data-testid="grant-suite-workspace">
      <ModuleWorkspaceHeader
        title="Grants"
        hideTitle
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {tab === "discover" && municipalityId ? (
              <Button variant="outline" size="sm" onClick={() => setProfileWizardOpen(true)}>
                <Settings2 className="mr-2 h-4 w-4" />
                {profileComplete ? "Edit profile" : "Set up profile"}
              </Button>
            ) : null}
            <MunicipalitySwitcher />
          </div>
        }
        meta={
          <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
            <h1 className="text-lg font-bold sm:text-xl">Grants</h1>
            <div role="group" aria-label="Grants views" className="inline-flex flex-wrap items-center gap-1 rounded-lg border bg-muted p-1">
            {visibleTabs.map((item) => {
              const Icon = item.icon;
              const isLocked = !DISCOVER_TIER_TABS.includes(item.id) && !canUseGrantSuite;
              return (
                <div
                  key={item.id}
                  className={cn("relative", isLocked && "group")}
                  onMouseEnter={isLocked ? () => setLockedHintTab(item.id) : undefined}
                  onMouseLeave={isLocked ? () => setLockedHintTab(null) : undefined}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-pressed={tab === item.id}
                    aria-disabled={isLocked || undefined}
                    data-locked={isLocked || undefined}
                    className={cn(
                      "h-7 px-3 text-muted-foreground hover:bg-background/60 hover:text-foreground",
                      tab === item.id && "bg-background text-foreground shadow-sm hover:bg-background",
                      isLocked && "opacity-60",
                    )}
                    onClick={() => selectTab(item.id)}
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    {item.label}
                  </Button>
                  {isLocked && lockedHintTab === item.id ? (
                    <LaunchPadUnavailableHint className="left-0 right-auto min-w-[14rem]" />
                  ) : null}
                </div>
              );
            })}
            </div>
          </div>
        }
        density="compact"
      />

      <ModuleWorkspaceBody
        className={cn(
          "flex min-h-0 flex-1 flex-col overflow-hidden",
          tab === "track" && "!p-0",
        )}
        contentClassName="flex h-full min-h-0 flex-1 flex-col"
      >
        {tab === "discover" ? (
          <GrantDiscoverPanel
            onSavedToWatchlist={canUseGrantSuite ? () => setTab("track") : undefined}
            profileWizardOpen={profileWizardOpen}
            onProfileWizardOpenChange={setProfileWizardOpen}
          />
        ) : tab === "radar" && municipalityId ? (
          <GrantRadarPanel key={municipalityId} municipalityId={municipalityId} />
        ) : tab === "my_milestones" ? (
          <MyMilestonesPanel />
        ) : tab === "team_manager" ? (
          <GrantTeamManagerPanel />
        ) : (
          <GrantTrackingPanel
            defaultFilter="pursuing"
            onGoToDiscover={grantRadar ? undefined : () => setTab("discover")}
            focusRequest={focusRequest}
            onFocusRequestHandled={clearGrantOpportunityFocus}
          />
        )}
      </ModuleWorkspaceBody>
    </div>
  );
}
